import { useParams, Link } from "react-router-dom";
import { channels, hotKeywords, siteMeta } from "@/mocks/chigua";
import { getChannel, channelArticles, getArticlesBySlugs } from "@/lib/chiguaData";
import useSeo from "@/hooks/useSeo";
import Breadcrumbs from "@/components/chigua/Breadcrumbs";
import StoryCard from "@/components/chigua/StoryCard";
import JsonLd from "@/components/chigua/JsonLd";

export default function ChannelList() {
  const { slug = "" } = useParams();
  const channel = getChannel(slug);
  const items = channelArticles(slug);
  const others = channels.filter((c) => c.slug !== slug);
  const hotPicks = getArticlesBySlugs(
    [...items].sort((a, b) => b.heat - a.heat).slice(0, 3).map((a) => a.slug)
  );

  useSeo({
    title: channel
      ? `${channel.name}-${channel.tagline} 今日吃瓜`
      : "栏目不存在 - 今日吃瓜",
    description: channel ? channel.intro : "您访问的栏目不存在，请返回首页浏览其他内容。",
    path: `/list/${slug}`,
    keywords: `今日吃瓜,${channel ? channel.name : ""},内幕深扒,内幕真相`,
  });

  if (!channel) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center md:px-6">
        <p className="text-7xl font-black text-background-300">404</p>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">该栏目不存在或已被调整</h1>
        <p className="mt-3 text-foreground-600">不妨返回首页，浏览正在更新的深扒与热点内容。</p>
        <Link to="/" className="mt-6 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50">
          返回首页
        </Link>
      </div>
    );
  }

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${channel.name}-${channel.tagline}`,
      description: channel.intro,
      url: `${siteMeta.domain}/list/${slug}`,
      inLanguage: "zh-CN",
      isPartOf: { "@type": "WebSite", name: siteMeta.brand, url: siteMeta.domain },
      about: `${channel.name}相关圈内热点与内幕深扒复盘`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${siteMeta.domain}/` },
        { "@type": "ListItem", position: 2, name: channel.name, item: `${siteMeta.domain}/list/${slug}` },
      ],
    },
  ];

  return (
    <div className="mx-auto max-w-[1400px] px-4 md:px-6">
      <Breadcrumbs items={[{ label: channel.name }]} />
      <JsonLd data={schema} />

      {/* 栏目头 */}
      <section className="overflow-hidden rounded-xl border border-background-200 bg-foreground-950 text-background-100">
        <div className="flex flex-col gap-6 p-7 md:flex-row md:items-center md:justify-between md:p-10">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded bg-secondary-500/90 px-2.5 py-1 text-xs font-semibold text-foreground-950">
              <i className="ri-archive-line" aria-hidden="true" />
              {channel.label} · 栏目
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-4xl">{channel.name}</h1>
            <p className="mt-3 text-sm leading-6 text-background-300 md:text-[15px]">{channel.intro}</p>
            <p className="mt-2 text-xs text-background-500">{channel.tagline}</p>
          </div>
          <div className="shrink-0 text-center md:text-right">
            <p className="font-display-sc text-6xl font-bold text-secondary-300 md:text-7xl">{items.length}</p>
            <p className="text-xs text-background-400">篇已收录稿件</p>
          </div>
        </div>
      </section>

      <div className="mt-8 grid gap-8 pb-16 lg:grid-cols-12">
        {/* 文章列表 */}
        <section className="lg:col-span-8">
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-foreground-800">
            <i className="ri-article-line text-primary-600" aria-hidden="true" />
            全部稿件
            <span className="text-foreground-400">({items.length})</span>
          </div>
          {items.length ? (
            <div className="space-y-6 rounded-xl border border-background-200 bg-background-50 p-6 md:p-7">
              {items.map((a) => (
                <StoryCard key={a.slug} item={a} />
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-background-200 bg-background-50 p-10 text-center text-foreground-500">
              该栏目正在筹备中，敬请期待。
            </div>
          )}
        </section>

        {/* 侧栏 */}
        <aside className="space-y-6 lg:col-span-4">
          {hotPicks.length > 0 && (
            <div className="rounded-xl border border-background-200 bg-background-50 p-6">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-fire-line text-accent-500" aria-hidden="true" />
                本栏热读
              </h3>
              <ul className="mt-3 space-y-3">
                {hotPicks.map((a) => (
                  <li key={a.slug}>
                    <Link to={`/story/${a.slug}`} className="group flex items-start gap-3">
                      <span className="mt-1 grid h-7 w-7 shrink-0 place-items-center rounded bg-primary-100 text-xs font-bold text-primary-700">
                        {a.kind}
                      </span>
                      <span className="line-clamp-2 text-sm font-medium text-foreground-800 transition group-hover:text-primary-700">
                        {a.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="rounded-xl border border-background-200 bg-background-50 p-6">
            <h3 className="font-heading text-base font-bold text-foreground-950">其他栏目</h3>
            <ul className="mt-4 space-y-2">
              {others.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/list/${c.slug}`}
                    className="flex items-center justify-between rounded-md px-3 py-2.5 text-sm text-foreground-700 transition hover:bg-primary-100/60 hover:text-primary-700"
                  >
                    <span className="inline-flex items-center gap-2">
                      <span className="text-xs text-foreground-400">{c.label}</span>
                      {c.name}
                    </span>
                    <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-xl border border-secondary-200 bg-secondary-100/40 p-6">
            <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-price-tag-3-line text-secondary-700" aria-hidden="true" />
              当前热词
            </h3>
            <div className="mt-4 flex flex-wrap gap-2">
              {hotKeywords.map((k) => (
                <span key={k} className="rounded-full border border-secondary-200 bg-background-50 px-3 py-1 text-xs text-foreground-700">
                  {k}
                </span>
              ))}
            </div>
          </div>

          <div className="rounded-xl bg-primary-500 p-6 text-background-50">
            <h3 className="flex items-center gap-2 font-heading text-lg font-bold">
              <i className="ri-megaphone-line" aria-hidden="true" />
              投稿与线索
            </h3>
            <p className="mt-2 text-sm leading-6 text-background-100">
              掌握与圈内事件相关的公开线索？欢迎来稿，我们一起把时间线拼完整。
            </p>
            <Link to="/contact" className="mt-4 inline-flex whitespace-nowrap rounded-md bg-background-50 px-4 py-2.5 text-sm font-semibold text-primary-700 transition hover:bg-background-100">
              提交线索
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}